import config from "../_config/config.const";
import { cacheRegistrationPrefill, mapApplyOnResponse } from "./client-registration-token.helper";

/** Client portal base URL (VITE brand app), never a comma-joined list. */
export function getClientAppBaseUrl(): string {
    const raw = (config as { CLIENTAPP?: string | string[] }).CLIENTAPP;

    if (typeof raw === "string" && raw.trim()) {
        return raw.replace(/\/+$/, "");
    }

    if (Array.isArray(raw)) {
        const first = raw.map((v) => String(v).trim()).find(Boolean);
        if (first) return first.replace(/\/+$/, "");
    }

    return "";
}

function buildPortalQuery(params: {
    brandId: number;
    quoteId: number;
    quoteProviderPriceId?: number;
    accountExists?: boolean;
    resumeApply?: boolean;
}) {
    if (!(params.brandId > 0)) {
        throw new Error("brandId is required for Client Portal redirects on the shared domain.");
    }
    const qs = new URLSearchParams();
    qs.set("brandId", String(params.brandId));
    qs.set("quoteId", String(params.quoteId));
    if (params.quoteProviderPriceId != null && params.quoteProviderPriceId > 0) {
        qs.set("quoteProviderPriceId", String(params.quoteProviderPriceId));
    }
    if (params.accountExists) qs.set("accountExists", "1");
    if (params.resumeApply) qs.set("resumeApply", "1");
    return qs;
}

export function buildClientRegistrationAbsoluteUrl(baseUrl: string, registrationToken: string) {
    const base = baseUrl.replace(/\/+$/, "");
    return `${base}/client-registration?registrationToken=${encodeURIComponent(registrationToken)}`;
}

export function redirectToClientPortalAfterApply(
    json: unknown,
    context?: { quoteId: number; brandId: number },
) {
    const result = mapApplyOnResponse(json);
    const base = getClientAppBaseUrl();
    if (!base) throw new Error("Client portal URL is not configured.");
    if (!(context?.brandId != null && context.brandId > 0)) {
        throw new Error("brandId is required for Client Portal redirects on the shared domain.");
    }

    let targetUrl = "";
    const quoteId = result.quoteId || context?.quoteId || 0;
    const brandId = context.brandId;
    const priceId = result.quoteProviderPriceId;

    if (result.canProceedWithApplication) {
        const qs = buildPortalQuery({
            brandId,
            quoteId,
            quoteProviderPriceId: priceId,
            resumeApply: true,
        });
        targetUrl = `${base}/?${qs.toString()}`;
    } else if (result.isClientUser) {
        const qs = buildPortalQuery({
            brandId,
            quoteId,
            quoteProviderPriceId: priceId,
            resumeApply: true,
            accountExists: true,
        });
        targetUrl = `${base}/login?${qs.toString()}`;
    } else if (result.registrationToken && result.prefill) {
        cacheRegistrationPrefill(result.registrationToken, result.prefill);
        targetUrl = buildClientRegistrationAbsoluteUrl(base, result.registrationToken);
    } else {
        throw new Error("Could not start registration. Please try again.");
    }

    try {
        if (window.top && window.top !== window.self) {
            window.top.location.href = targetUrl;
        } else {
            window.location.href = targetUrl;
        }
    } catch {
        window.location.href = targetUrl;
    }
}
